import { motion } from "framer-motion";
import { useEffect } from "react";
// import heroVideo from "../assests/automotive-hero.mp4"; 
import car1 from "../assests/car.jpg"; // First car image
import car2 from "../assests/car1.jpg"; // Second car image

export default function AutomotiveAI() {
   
  return (
    <div className="bg-gray-50 text-gray-900 font-orbitron">

      {/* Hero Section */}
      <section className="relative w-full h-[70vh] flex items-center justify-center text-center overflow-hidden">
        {/* <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video> */}
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/70 via-orange-500/50 to-yellow-500/70"></div>

        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10 px-6 max-w-3xl"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-white drop-shadow-lg">
            Automotive AI & Data Solutions
          </h1>
          <p className="text-lg md:text-xl text-gray-100 leading-relaxed">
            Drive innovation in automotive AI with high-quality datasets, image/video annotations, and predictive analytics.
          </p>
        </motion.div>

        {/* Car Images */}
        <motion.div 
          className="absolute bottom-5 left-1/4 flex gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <img src={car1} alt="Car 1" className="w-40 rounded-lg shadow-xl" />
          <img src={car2} alt="Car 2" className="w-40 rounded-lg shadow-xl" />
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-20 px-6 text-center max-w-4xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-semibold mb-6 text-red-700"
        >
          Why Automotive AI Matters
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-lg text-gray-700 leading-relaxed"
        >
          AI is transforming the automotive industry — from autonomous vehicles to predictive maintenance. High-quality, annotated datasets ensure accurate perception, decision-making, and safety in AI systems.
        </motion.p>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-yellow-50 via-white to-red-50">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-semibold text-center mb-12 text-red-700"
        >
          Our Automotive Services
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            { title: "🚗 Vehicle Image Annotation", desc: "Label cars, trucks, and other vehicles for computer vision models.", color: "from-red-400 to-orange-500" },
            { title: "🛣 Road Scene Segmentation", desc: "Identify lanes, traffic signs, and obstacles accurately for autonomous AI.", color: "from-yellow-400 to-red-500" },
            { title: "🎥 Dashcam Video Annotation", desc: "Annotate driving footage for behavior analysis and safety testing.", color: "from-orange-400 to-yellow-500" },
            { title: "🔧 Predictive Maintenance Data", desc: "Prepare datasets for AI-driven vehicle diagnostics and maintenance.", color: "from-pink-400 to-red-500" },
            { title: "🌍 Multilingual Navigation Data", desc: "GPS, road instructions, and multilingual datasets for global deployment.", color: "from-teal-400 to-green-500" },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className={`bg-gradient-to-br ${item.color} p-6 rounded-2xl shadow-lg text-white hover:shadow-2xl transition`}
            >
              <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
              <p className="leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-semibold text-center mb-12 text-red-700"
        >
          Our Workflow
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 max-w-6xl mx-auto text-center">
          {[
            "Gather vehicle & road data",
            "Annotate images & videos",
            "QA with automotive experts",
            "Integrate into AI systems",
            "Deliver high-quality datasets",
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="p-6 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl shadow-md hover:shadow-lg"
            >
              <div className="text-4xl mb-3">{`${idx + 1}️⃣`}</div>
              <p className="text-gray-800 leading-relaxed">{step}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-center bg-gradient-to-r from-red-600 to-orange-600 text-white">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-5xl font-bold mb-6"
        >
          Drive AI Innovation in Automotive
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-lg max-w-xl mx-auto mb-6"
        >
          Partner with us to access high-quality automotive datasets and expert annotation services for autonomous vehicles, predictive maintenance, and AI-powered transportation solutions.
        </motion.p>
        <motion.button
          whileHover={{ scale: 1.1 }}
          className="bg-white text-red-700 font-semibold px-6 py-3 rounded-xl shadow-md hover:bg-gray-100 transition"
        >
          Get Started Today
        </motion.button>
      </section>
    </div>
  );
}
